import type { TaskDecisionPrompt } from '@wix/ai-scheduling-assistant-common-lib/utils';
import { bucketOfTask, orderedDistinctAgentIds, sortedForTaskList, workingCounts, type BucketableTask } from './agent-task-buckets';

// The shared view-model for the island quick-view (the pill-anchored bubble on every surface). Pure and
// mirrored 1:1 on native so all three show the same rows in the same order.
//
// **The two tones no longer open the same rows, and neither one is an all-status peek.** Each card lists
// exactly what its own pill claims:
//   • BLUE  — "{n} agents at work": the tasks with a VERIFIED live run (`buildWorkingRows`).
//   • ORANGE — "{n} Action needed": the tasks in the WAITING bucket and nothing else (`rows` below).
// That was not always so. The orange card used to render an all-status peek, which is what produced the
// reported screen: a header reading "1 Action needed" above one IN_PROGRESS row, one ACTION_NEEDED row and
// a folded "2 Done" row. A card whose header states a number and whose body lists three other things makes
// the reader do arithmetic that cannot come out.
//
// **The rows are still a PROJECTION of the account-wide Tasks list, not a second list** — the same set the
// Tasks screen renders (outreach rows included), the same recency window, the same reading order
// (`sortedForTaskList`: unfinished above finished, newest-updated within each half) and the same origin
// fold. Only the POPULATION narrows, and it narrows at this builder's own input: the shared order/window/
// fold rules in `agent-task-buckets` are used, never modified, so the Tasks screen — which composes those
// same rules directly — cannot be touched by anything here. This builder feeds the island card and nothing
// else (verified: no other caller), which is why the filter can live in it at all.
//
// Capped to a peek with an overflow count and a "see all" footer. Each row carries enough to read at a
// glance — status, title, the Space it lives in, its agent, and a short summary. The row mapping happens
// at the call site.

export interface QuickViewTask extends BucketableTask {
  id: string;
  title: string;
  /** Required here (widened from BucketableTask's optional) so the row's StatusBadge always has a value. */
  status: string;
  agentId?: string;
  workspaceId: string;
  workspaceName: string;
  /** A short "what it's about" line, when the task carries one. */
  summary?: string;
  /**
   * This row is an inbound OUTREACH, not an AgentTask — a message from another person's agent, which the
   * account-wide list files beside the tasks (see `/api/tasks`'s `outreachToWebTask`). It bucket, badges
   * and sorts like any task; the flag only tells the row how to title itself and what a click opens,
   * exactly as `TaskCard` branches on `rowKind`. Mirrors iOS/Android `SymphonyTask.isOutreach`.
   */
  isOutreach?: boolean;
  /** Who sent it — an outreach row is titled by the person, since its subject is a person not a task. */
  senderName?: string;
  /** The FILE this outreach announces, when it is a share rather than a message (`Outreach.shared_file`).
   *  Presence is the marker the row branches on — see `WebTask.sharedFile`. Mirrors iOS/Android. */
  sharedFile?: { fileId: string; fileName?: string; shareLink?: string };
  /** The routine (pulse identity) whose firing created this task; absent for one-off work. Carried so
   *  the peek can fold consecutive runs of one routine, exactly as the Tasks screen does. */
  pulseIdentityId?: string;
  /** The goal whose plan laid this task out; absent on work that serves no goal. Carried for the same
   *  reason as `pulseIdentityId`: the peek folds BOTH origins, at the one shared threshold, or a plan's
   *  tasks read as one folded line on the Tasks screen and as several loose rows in the bubble. */
  goalId?: string;
  /** The agent's own present-continuous status line — the working row's hero sentence when present.
   *  Empty string means absent (the mapping normalizes it away). */
  currentAction?: string;
  /** The Master Plan steps, for the sentence chain's second tier: the first NOT-completed step title. */
  plan?: { title: string; completed: boolean }[];
  /** Newest write on the task's own conversations, ISO — the working rows' sort key and elapsed stamp.
   *  Absent when the list did not compute run state for this row. */
  lastActivityAt?: string;
  /** Widened from `BucketableTask`'s structural minimum (`{decisionId}[]`) to the full prompt, so the
   *  same `TaskDecisionPrompts` block that answers the list row can answer the peek row too — one
   *  implementation, not a second one shaped for this surface's own narrower type. */
  decisionPrompts?: TaskDecisionPrompt[];
}

/**
 * One rendered row of the peek: a task, or consecutive tasks of ONE ORIGIN — a routine's runs, or a goal's
 * laid-out plan — collapsed behind their newest. `runCount` is 1 on an ordinary row; on a collapsed row the
 * fields are the NEWEST task's and `runCount` says how many it stands for — the same fold
 * (`groupTaskOrigins`) the Tasks screen renders as `TaskOriginGroup`, so neither an hourly routine nor a
 * ten-task goal can fill the whole peek on its own.
 */
export interface QuickViewRow extends QuickViewTask {
  runCount: number;
}

export interface IslandQuickView {
  /** The ORANGE card's rows — the ACTION-REQUIRED tasks (the `waiting` bucket) in the Tasks list's reading
   *  order, same-origin runs folded, capped to `QUICK_VIEW_SHOWN`. Nothing else appears here: the card's
   *  header counts action-needed tasks, so its body may only contain action-needed tasks. */
  rows: QuickViewRow[];
  /** Action-required TASKS the shown rows do not stand for → the overflow row's "{n} more tasks awaiting
   *  your input". Counted in TASKS, not rows, deliberately: the header counts tasks, so `shown tasks +
   *  moreCount === waitingCount` holds by construction and the card's arithmetic reads correctly against
   *  it. (A fold hides its runs INSIDE a shown row, so those runs are not "more" — they are already on
   *  screen, behind a row that says how many it stands for.) */
  moreCount: number;
  /** Every action-required task in the window — the number the card's own body accounts for. See the note
   *  on `moreCount`: this is the total the rows and the overflow add up to. */
  waitingCount: number;
  /**
   * The FULL windowed task list in reading order. The agent-started announcement names its agent from it,
   * and the blue card's working rows are selected out of it.
   */
  sorted: QuickViewTask[];
  /** The blue "agents at work" card: the working rows (live runs only), their overflow, and the counts. */
  working: WorkingRows;
  /**
   * The blue pill's leading glyphs — the DISTINCT working agents in activity (first-seen) order, one id
   * per glyph. `''` is the Maestro / agent-less bucket (a missing `agentId`, or a maestro action folded
   * into it). Mirrors `working.workingAgentIds`, surfaced at the top level so the island hook reads both
   * id lists from one place; `workingAgentIds.length === working.workingAgents`. Web derives this
   * client-side; the phones read the same per-agent order off the server aggregate.
   */
  workingAgentIds: string[];
  /**
   * The orange pill's leading glyphs — the DISTINCT action-required agents in the Tasks list's reading
   * order, one id per glyph. `''` is the Maestro / agent-less bucket. Taken over the same `actionable`
   * slice the orange card lists, so the pill's glyphs and the card's rows describe the same agents.
   */
  waitingAgentIds: string[];
}

/**
 * The peek size — how many rows EITHER card shows before its overflow row, and deliberately ONE constant
 * for both tones.
 *
 * It used to be two: 6 for the orange card, 3 for the blue one. Two numbers for one visual cap is how they
 * drifted apart in the first place, and six was the wrong one — because the orange rows FOLD (a routine's
 * runs, a goal's plan collapse behind their newest), six rows routinely stood for ten or more tasks. The
 * card stopped being a peek and became a second task list, directly above the "All tasks" button that
 * opens the real one. Three rows still say who is waiting and on what; the remainder is already accounted
 * for, in TASKS, by the overflow row.
 */
export const QUICK_VIEW_SHOWN = 3;

/**
 * One "Maestro action" — the Maestro at work on something that is NOT a task: a delegated external job
 * (a Wixel render) or a live free-run agent loop with no task row behind it. Web reads these off
 * `/api/tasks`'s `maestroActions` sibling (the same two conversation reads the server aggregate performs);
 * the phones read them off the aggregate itself. Absent field on the wire ≡ empty — an old server simply
 * has no actions to claim.
 */
export interface MaestroActionRow {
  /** The machine kind — 'wixel', 'agent-run', … Unknown kinds still render, on the generic fallback. */
  kind: string;
  /** Short user-facing line derived from the brief / the conversation name. Empty means absent. */
  label?: string;
  /** ISO instant the job was accepted — the row's elapsed stamp, when present. */
  startedAt?: string;
}

/**
 * The action row's hero sentence: its own `label` when it has one, else a localized per-kind fallback —
 * NEVER an empty line. Returns the copy KEY for the fallback because localization is the render site's;
 * an unknown/kind-less entry lands on the generic "Working for you".
 */
export type MaestroActionSentence = { kind: 'label'; text: string } | { kind: 'fallback'; key: 'agentsIsland.action.wixel' | 'agentsIsland.action.working' };

export function maestroActionSentence(action: Pick<MaestroActionRow, 'kind' | 'label'>): MaestroActionSentence {
  const label = action.label?.trim();
  if (label) return { kind: 'label', text: label };
  return { kind: 'fallback', key: action.kind === 'wixel' ? 'agentsIsland.action.wixel' : 'agentsIsland.action.working' };
}

export interface WorkingRows {
  /** Maestro-action rows — rendered FIRST (before the task rows), sharing the `QUICK_VIEW_SHOWN` cap. */
  actionRows: MaestroActionRow[];
  /** One row per WORKING task (`runInFlight === true`), newest activity first, filling what the cap has
   *  left after the action rows. */
  rows: QuickViewTask[];
  /** Working things beyond the shown rows (actions AND tasks) → the overflow row's count. */
  moreCount: number;
  /** ALL working things in the loaded list — tasks with a live run PLUS the maestro actions. */
  workingCount: number;
  /** DISTINCT agents behind the working tasks (missing `agentId` folds into one Maestro bucket, and any
   *  maestro action folds into that SAME bucket) — the blue pill's own count. */
  workingAgents: number;
  /** Those same distinct agents as an ordered id list (activity DESC, `''` for the Maestro bucket) — one
   *  id per glyph on the blue pill. `workingAgentIds.length === workingAgents`. */
  workingAgentIds: string[];
}

/** `lastActivityAt` as a sortable number; a row that carries none sorts to the TAIL (epoch 0), exactly as
 *  both phones do — no `updatedDate` fallback, so all three surfaces break the tie the same way. */
function activityMs(task: QuickViewTask): number {
  if (task.lastActivityAt) {
    const ms = Date.parse(task.lastActivityAt);
    if (!Number.isNaN(ms)) return ms;
  }
  return 0;
}

/**
 * The blue card's rows: ONLY tasks whose run is verifiably in flight (`runInFlight === true` — absent is
 * "not computed" and must select nothing), sorted by `lastActivityAt` DESC so the most recently active
 * agent reads first, capped to three with an overflow count. No origin fold here: a working row is a live
 * loop, and two live runs of one routine are genuinely two agents' worth of work happening now.
 *
 * `actions` are the Maestro's own in-flight non-task work. They render FIRST (the card's subject is who is
 * working right now, and an action is the Maestro personally at it), share the same three-row cap, count
 * into `workingCount` and the overflow, and fold into the ONE Maestro bucket `workingAgents` already keeps
 * for agent-less tasks — an action beside an agent-less working task is still one agent, never two.
 */
export function buildWorkingRows(tasks: QuickViewTask[], actions: MaestroActionRow[] = []): WorkingRows {
  const working = tasks.filter((task) => task.runInFlight === true);
  const sorted = [...working].sort((a, b) => activityMs(b) - activityMs(a));
  const counts = workingCounts(sorted, actions.length);
  const actionRows = actions.slice(0, QUICK_VIEW_SHOWN);
  const rows = sorted.slice(0, Math.max(0, QUICK_VIEW_SHOWN - actionRows.length));
  // One id per distinct working agent, in the same activity order the count was taken. A maestro action
  // has no task row of its own, so fold its worker — the Maestro (`''`) — in explicitly, exactly as
  // `workingCounts` folds it into `workingAgents`.
  const workingAgentIds = orderedDistinctAgentIds(sorted);
  if (actions.length > 0 && !workingAgentIds.includes('')) {
    workingAgentIds.push('');
  }
  return {
    actionRows,
    rows,
    moreCount: Math.max(0, actions.length + sorted.length - actionRows.length - rows.length),
    workingAgentIds,
    ...counts,
  };
}

/**
 * The working row's hero sentence, by the shared fallback chain: the agent's own `currentAction` →
 * the first NOT-completed plan step title → the localized "Working on: {title}" fallback. Returns the
 * SOURCE rather than a final string because the last tier is the caller's to localize — and because the
 * row's secondary title line is OMITTED exactly when the sentence already is the title (`kind: 'title'`),
 * so the two decisions must come from one answer.
 */
export type WorkingSentence = { kind: 'action' | 'step'; text: string } | { kind: 'title' };

export function workingSentence(task: Pick<QuickViewTask, 'currentAction' | 'plan'>): WorkingSentence {
  const action = task.currentAction?.trim();
  if (action) return { kind: 'action', text: action };
  const step = (task.plan ?? []).find((s) => !s.completed && s.title.trim());
  if (step) return { kind: 'step', text: step.title.trim() };
  return { kind: 'title' };
}

/**
 * Is this task ASKING THE USER FOR SOMETHING — the orange pill's own population (`ACTION_NEEDED` /
 * `PAUSED`, via the one shared bucket map). The card's rows are exactly this predicate applied to the
 * account-wide list, so the rows and the header can only ever describe the same kind of thing.
 */
export function isActionRequired(task: QuickViewTask): boolean {
  return bucketOfTask(task) === 'waiting';
}

export function buildIslandQuickView(tasks: QuickViewTask[], now: number = Date.now(), actions: MaestroActionRow[] = []): IslandQuickView {
  // The Tasks list's own order — NOT a local recency sort. See the header note. The FULL list, because
  // `sorted` also feeds the blue card (its working rows and the Maestro's own actions) and the
  // agent-started announcement.
  const sorted = sortedForTaskList(tasks, now);
  // The orange card's population, narrowed BEFORE the fold. Filtering first is what makes a folded row
  // honest here: `groupTaskOrigins` collapses CONSECUTIVE same-origin, same-outcome tasks, so on the
  // filtered list a fold can only ever stand for several ACTION-NEEDED runs of one routine or goal — "2
  // Action needed", never the "2 Done" deck that was showing under an "Action needed" header. A fold of
  // finished runs is not a smaller version of an action-required row; it is a different claim, and the
  // reader taps it expecting the thing the card promised.
  const actionable = sorted.filter(isActionRequired);
  // NO origin fold on the orange card anymore (redesign, Figma 17339-142828 / 17666-231716): each
  // action-required task is its own row, carrying a small "Routine"/"Goal" origin word on its meta line
  // instead of collapsing consecutive same-origin runs behind one deck. `runCount` stays 1 so the
  // shownTasks/moreCount arithmetic (and its `=== waitingCount` invariant) is unchanged.
  const rows = actionable.map((task) => ({ ...task, runCount: 1 }));
  const shown = rows.slice(0, QUICK_VIEW_SHOWN);
  const shownTasks = shown.reduce((total, row) => total + row.runCount, 0);
  // The orange pill's glyphs, over the SAME action-required slice its card lists (before the origin fold,
  // so a folded routine still contributes its agent once) — reading order preserved, deduped.
  const waitingAgentIds = orderedDistinctAgentIds(actionable);
  const working = buildWorkingRows(sorted, actions);
  return {
    rows: shown,
    // Tasks, not rows — see `moreCount`. `shownTasks + moreCount === waitingCount` is the invariant the
    // card's body is read against.
    moreCount: Math.max(0, actionable.length - shownTasks),
    waitingCount: actionable.length,
    sorted,
    working,
    workingAgentIds: working.workingAgentIds,
    waitingAgentIds,
  };
}


/** The fields {@link toQuickViewTask} reads off a wire task. Structural on purpose: `lib/tasks.ts`
 *  reaches the app graph, and this module must stay importable from a plain unit test. */
export type WireTaskForQuickView = Partial<Omit<QuickViewTask, 'workspaceName' | 'plan'>> & {
  rowKind?: string;
  plan?: QuickViewTask['plan'];
};

/**
 * One task as the island's peek holds it — the ONLY projection from the wire row onto the view-model.
 *
 * It lives HERE, beside the rules that consume it, rather than inside `useIslandQuickView`, because a
 * field dropped in this mapping is invisible: every component test still passes (the component is fine),
 * every hook test still passes (the hook is fine), and the feature is simply dead. That happened — the
 * share notice reached `QuickViewTask`'s TYPE and the row's code, and the mapping never copied it, so
 * the island went on offering "Reply" on a file share. A pure function is one a test can call.
 *
 * `workspaceName` is resolved by the caller, which is the only thing here that needs the workspace list.
 */
export function toQuickViewTask(task: WireTaskForQuickView, workspaceName: string): QuickViewTask {
  return {
    id: task.id ?? '',
    title: (task.title ?? '').trim(),
    status: task.status ?? '',
    agentId: task.agentId,
    // Both empty on an outreach row (an Outreach belongs to no Space) — the row simply shows no Space.
    workspaceId: task.workspaceId ?? '',
    // Resolved by the caller, which owns the workspace list; an outreach has no Space, so empty.
    workspaceName,
    summary: task.summary?.trim() || undefined,
    updatedDate: task.updatedDate,
    // Carried so the peek folds consecutive tasks of one origin exactly where the Tasks screen does —
    // a routine's runs, or a goal's laid-out plan.
    pulseIdentityId: task.pulseIdentityId || undefined,
    goalId: task.goalId || undefined,
    // The two fields the derived "laid out and never started" rule reads (`isNotStartedTask`), so the
    // bubble cannot show "In progress" on a row the Tasks screen shows as Not started.
    notBefore: task.notBefore || undefined,
    agentConversationId: task.agentConversationId || undefined,
    // Carried so the row can title and open itself the way `TaskCard` does — an outreach has no task
    // detail behind it, so a click must open the thread, not a modal with nothing to show.
    ...(task.rowKind === 'outreach'
      ? {
          isOutreach: true,
          senderName: task.senderName,
          // The share notice, WITHOUT which the row has no way to know it is about a file and
          // falls back to offering Reply — the whole defect this feature fixes, reintroduced one
          // layer below the component that was supposed to fix it. Presence is the marker, so it
          // is spread in only when it names a file.
          ...(task.sharedFile ? { sharedFile: task.sharedFile } : {}),
        }
      : {}),
    // Tri-state, and the spread is what preserves it: only a boolean crosses, so an absent field on the
    // wire stays ABSENT on the view-model row — "not computed", never false. Same for `lastActivityAt`.
    // The open questions this row is blocked on, carried for the SAME reason the fold keys are: the
    // orange card's population is `bucketOfTask(task) === 'waiting'`, and that verdict now depends on
    // these. A view-model that dropped them would leave the bubble listing a different set of rows than
    // the Tasks screen it peeks at, which is the drift this projection exists to prevent. Absence-
    // preserving, like the two tri-states below it — never `[]`, never `false`.
    ...(task.decisionPrompts?.length ? { decisionPrompts: task.decisionPrompts } : {}),
    ...(task.decisionPromptsUnavailable ? { decisionPromptsUnavailable: true as const } : {}),
    ...(typeof task.runInFlight === 'boolean' ? { runInFlight: task.runInFlight } : {}),
    ...(typeof task.lastActivityAt === 'string' && task.lastActivityAt ? { lastActivityAt: task.lastActivityAt } : {}),
    // The working row's sentence chain: the agent's own status line (empty string means absent), then
    // the first unticked plan step, then the localized "Working on: {title}" at the render site.
    currentAction: task.currentAction?.trim() || undefined,
    plan: task.plan ?? [],
  };
}
